import { useState } from "react";
import {
  Button,
  Center,
  Group,
  Modal,
  SegmentedControl,
  Stack,
  Text,
} from "@mantine/core";
import {
  IconBarcode,
  IconClockPlay,
  IconDeviceMobile,
  IconHandClick,
  IconPrinter,
  IconUserCheck,
} from "@tabler/icons-react";

import StationPrintSetup from "./StationPrintSetup";
import StationScannerSetup from "./StationScannerSetup";

const option = (value, Icon, label) => ({
  value,
  label: (
    <Center style={{ gap: 8 }}>
      <Icon size={18} />
      <span>{label}</span>
    </Center>
  ),
});

const Section = ({ title, description, children }) => (
  <Stack gap={6}>
    <div>
      <Text fw={500} size="sm">
        {title}
      </Text>
      <Text c="dimmed" size="xs">
        {description}
      </Text>
    </div>
    {children}
  </Stack>
);

const StationSettingsModal = ({
  initialPrintingEnabled,
  initialAutoPrintEnabled,
  initialScannerEnabled,
  isLoading,
  onConfirm,
  onClose,
}) => {
  const [printingEnabled, setPrintingEnabled] = useState(
    !!initialPrintingEnabled
  );
  const [autoPrintEnabled, setAutoPrintEnabled] = useState(
    !!initialAutoPrintEnabled
  );
  const [scannerEnabled, setScannerEnabled] = useState(!!initialScannerEnabled);

  return (
    <Modal
      opened
      onClose={onClose}
      size="lg"
      title={<Text fw={600}>Station settings</Text>}
    >
      <Stack>
        <Section
          title="Scan source"
          description="How tickets are scanned at this station."
        >
          <SegmentedControl
            fullWidth
            value={String(scannerEnabled)}
            onChange={(value) => setScannerEnabled(value === "true")}
            data={[
              option("false", IconDeviceMobile, "Phone"),
              option("true", IconBarcode, "USB scanner"),
            ]}
          />
          {scannerEnabled && <StationScannerSetup />}
        </Section>
        <Section
          title="After scan"
          description="Either way the attendee is checked in."
        >
          <SegmentedControl
            fullWidth
            value={String(printingEnabled)}
            onChange={(value) => setPrintingEnabled(value === "true")}
            data={[
              option("true", IconPrinter, "Check in & print"),
              option("false", IconUserCheck, "Check in only"),
            ]}
          />
        </Section>
        {printingEnabled && (
          <>
            <Section
              title="Printing"
              description="Auto print starts 3 seconds after a ticket is scanned."
            >
              <SegmentedControl
                fullWidth
                value={String(autoPrintEnabled)}
                onChange={(value) => setAutoPrintEnabled(value === "true")}
                data={[
                  option("true", IconClockPlay, "Auto print"),
                  option("false", IconHandClick, "Manual print"),
                ]}
              />
            </Section>
            <StationPrintSetup />
          </>
        )}
        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              onConfirm({
                isPrintingEnabled: printingEnabled,
                isAutoPrintEnabled: printingEnabled && autoPrintEnabled,
                isScannerEnabled: scannerEnabled,
              })
            }
            loading={isLoading}
          >
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default StationSettingsModal;
